import { useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ALIGNMENTS, EPOCH_START, EPOCH_STOPS, formatYear, posFromYear } from "@/data/enhancements";

const EASE = [0.22, 1, 0.36, 1];
const TONE = [
  { hex: "#D4AF37", text: "text-[#D4AF37]", border: "border-[#D4AF37]/40", bg: "bg-[#D4AF37]" },
  { hex: "#38BDF8", text: "text-[#38BDF8]", border: "border-[#38BDF8]/40", bg: "bg-[#38BDF8]" },
];

function Column({ site, k, onRemove, onOpen }) {
  const t = TONE[k];
  return (
    <div data-testid={`compare-col-${k}`} className={`border ${t.border} bg-[#12141C]/90`}>
      <div className="relative aspect-[16/9] overflow-hidden">
        <img src={site.image} alt={site.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12141C] via-transparent to-transparent" />
        <span className={`absolute left-4 top-4 font-mono text-[9px] tracking-[0.35em] ${t.text}`}>
          {k === 0 ? "A" : "B"} — {site.category.toUpperCase()}
        </span>
        <button
          data-testid={`compare-remove-${k}`}
          onClick={() => onRemove(site.id)}
          aria-label={`Unpin ${site.name}`}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-white/25 bg-[#0B0C10]/70 font-serif text-base text-white/80 backdrop-blur transition-colors hover:border-[#FF9F1C] hover:text-[#FF9F1C]"
        >
          ×
        </button>
      </div>
      <div className="p-5 sm:p-6">
        <p className="font-mono text-[9px] tracking-[0.3em] text-white/45">{site.location.toUpperCase()}</p>
        <h3 data-testid={`compare-name-${k}`} className="mt-2 font-serif text-2xl leading-tight sm:text-3xl">
          {site.name}
        </h3>
        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 font-mono text-[10px] leading-relaxed">
          <div>
            <dt className="tracking-[0.25em] text-white/35">ORIGIN</dt>
            <dd className={t.text}>{formatYear(EPOCH_START[site.id])}</dd>
          </div>
          <div>
            <dt className="tracking-[0.25em] text-white/35">COORDS</dt>
            <dd className="text-white/70">{site.lat.toFixed(2)}° · {site.lng.toFixed(2)}°</dd>
          </div>
          <div>
            <dt className="tracking-[0.25em] text-white/35">SKY ALIGNMENT</dt>
            <dd className="text-white/70">{ALIGNMENTS[site.id]?.name || "— none recorded"}</dd>
          </div>
          <div>
            <dt className="tracking-[0.25em] text-white/35">DATING</dt>
            <dd className="text-white/70">{site.age}</dd>
          </div>
        </dl>
        <p className="mt-6 font-mono text-[9px] tracking-[0.3em] text-[#38BDF8]">MAINSTREAM</p>
        <p className="mt-2 text-[13px] font-light leading-relaxed text-white/65">{site.mainstream}</p>
        <p className={`mt-6 font-mono text-[9px] tracking-[0.3em] ${t.text}`}>HYPOTHESES</p>
        <ol className="mt-2 space-y-1.5">
          {site.hypotheses.map((h, i) => (
            <li key={h.title} className="flex items-baseline gap-3 font-serif text-base text-white/80">
              <span className="font-mono text-[9px] text-white/35">H.{String(i + 1).padStart(2, "0")}</span>
              {h.title}
            </li>
          ))}
        </ol>
        <p className="mt-6 font-mono text-[9px] tracking-[0.3em] text-[#FF9F1C]">ANOMALIES</p>
        <ul className="mt-2 space-y-1.5">
          {site.anomalies.map((a) => (
            <li key={a} className="flex items-baseline gap-3 font-mono text-[10px] leading-relaxed text-white/55">
              <span className="shrink-0 text-[#FF9F1C]">◦</span>
              {a}
            </li>
          ))}
        </ul>
        <button
          data-testid={`compare-open-${k}`}
          onClick={() => onOpen(site)}
          className={`mt-7 w-full border py-2.5 font-mono text-[9px] tracking-[0.28em] transition-all duration-300 ${t.border} ${t.text} hover:bg-white/5`}
        >
          OPEN FULL DOSSIER ↗
        </button>
      </div>
    </div>
  );
}

export default function CompareView({ pair, onClose, onRemove, onSwap, onOpen }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const gap = Math.abs(EPOCH_START[pair[0].id] - EPOCH_START[pair[1].id]);

  const copyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}#compare=${pair[0].id},${pair[1].id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Compare link copied — opens straight to this matchup");
    } catch {
      toast.error("Couldn't copy automatically — grab the URL bar instead");
    }
  };

  return (
    <motion.div
      data-testid="compare-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[80] overflow-y-auto bg-[#0B0C10]/92 backdrop-blur-md"
      data-lenis-prevent
    >
      <div className="mx-auto max-w-[1280px] px-5 pb-20 pt-24 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="flex flex-wrap items-end justify-between gap-4"
        >
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] text-[#D4AF37]">MYSTERY COMPARE</p>
            <h2 className="mt-3 font-serif text-3xl leading-tight sm:text-5xl">
              Two sites, <em className="italic text-[#D4AF37]">one timeline.</em>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="compare-copy-link"
              onClick={copyLink}
              className="border border-[#38BDF8]/50 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-[#38BDF8] transition-colors hover:bg-[#38BDF8] hover:text-[#0B0C10]"
            >
              COPY COMPARE LINK
            </button>
            <button
              data-testid="compare-swap"
              onClick={onSwap}
              className="border border-white/20 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-white/60 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              ⇄ SWAP
            </button>
            <button
              data-testid="compare-close"
              onClick={onClose}
              className="border border-[#D4AF37]/60 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0B0C10]"
            >
              CLOSE ×
            </button>
          </div>
        </motion.div>

        {/* shared timeline */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
          data-testid="compare-timeline"
          className="mt-10 border border-[#38BDF8]/25 bg-[#12141C]/85 px-5 pb-6 pt-5 backdrop-blur"
        >
          <div className="flex flex-wrap justify-between gap-2 font-mono text-[9px] tracking-[0.28em] text-white/50">
            <span>SHARED EPOCH SCALE</span>
            <span data-testid="compare-gap">
              GAP · <span className="text-[#FF9F1C]">{gap.toLocaleString()} YEARS</span>
            </span>
          </div>
          <div className="relative mt-10 h-px w-full bg-white/15">
            {EPOCH_STOPS.map((y) => (
              <span
                key={y}
                className="absolute top-0 -translate-x-1/2 font-mono text-[8px] tracking-[0.15em] text-white/30"
                style={{ left: `${posFromYear(y)}%` }}
              >
                <span className="mx-auto mb-1 block h-2 w-px bg-white/25" />
                <span className="hidden sm:block">{formatYear(y).replace(",", "")}</span>
              </span>
            ))}
            {pair.map((s, k) => (
              <span
                key={s.id}
                data-testid={`compare-marker-${k}`}
                className="absolute -translate-x-1/2"
                style={{ left: `${posFromYear(EPOCH_START[s.id])}%`, top: k === 0 ? "-26px" : "-26px" }}
              >
                <span
                  className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[8px] tracking-[0.2em] ${TONE[k].text}`}
                  style={{ top: k === 0 ? "-18px" : "50px" }}
                >
                  {k === 0 ? "A" : "B"} · {formatYear(EPOCH_START[s.id])}
                </span>
                <span className={`block h-[26px] w-px ${TONE[k].bg}`} style={{ marginTop: k === 0 ? 0 : "26px" }} />
                <span
                  className={`absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full ${TONE[k].bg}`}
                  style={{ top: "21px" }}
                />
              </span>
            ))}
          </div>
          <div className="h-14" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.18, ease: EASE }}
          className="mt-6 grid gap-5 md:grid-cols-2"
        >
          {pair.map((s, k) => (
            <Column key={s.id} site={s} k={k} onRemove={onRemove} onOpen={onOpen} />
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
}
